import React from 'react';
import { Modal } from '@stormwater-software/lib-ui';
import { Link } from 'react-router-dom';

export type CalendarInspection = {
  inspectionDOI: string;
  inspectionCertDate: string | null;
  inspectionStatus: string;
  inspectionType: string;
  inspector: string;
  clientName: string;
  projectName: string;
  projectId: number;
};

interface Props {
  show: boolean;
  onClose: () => void;
  dateLabel: string;
  inspections: CalendarInspection[];
  divisionId: number | string;
}

/**
 * SW2-2325 — every inspection submitted on a day, not just the first.
 *
 * The popover this replaces rendered `filteredInspection[0]`, so a day with more than
 * one inspection was already under-reported before any multi-project work: a single
 * project on two active forms was enough to hide one. Multi-select makes that the normal
 * case rather than an edge one.
 */
const CalendarDayModal: React.FC<Props> = ({
  show,
  onClose,
  dateLabel,
  inspections,
  divisionId,
}) => {
  if (!show) return null;

  /**
   * The title is just the date. The header's close control is
   * `position: absolute; right: 1em` over a centred, full-width title, so a long one
   * runs underneath it — "Inspections submitted — July 2, 2026" wrapped on a phone and
   * put the X on top of the year. The count moves into the body, and the span carries
   * padding so this cannot come back with a longer date format.
   */
  return (
    <Modal
      show={show}
      title={<span className='calendar-day-modal__title'>{dateLabel}</span>}
      handleClose={onClose}
      hideButtons
      dismissOnBgClick
      // `classes` lands on the backdrop, not the shell — see `_Calendar.scss`, which
      // reaches the shell through it to cap the height and keep the header in place.
      classes='calendar-day-modal-shell'
    >
      <div className='calendar-day-modal'>
        <p className='calendar-day-modal__count'>
          {inspections.length}{' '}
          {inspections.length === 1 ? 'inspection' : 'inspections'} submitted
        </p>

        <ul className='calendar-day-modal__list'>
          {inspections.map((inspection, index) => (
            <li
              className='calendar-day-modal__item'
              key={`${inspection.projectId}-${index}`}
            >
              <div className='calendar-day-modal__project'>
                {inspection.projectName}
              </div>
              {inspection.clientName && (
                <div className='calendar-day-modal__client'>
                  {inspection.clientName}
                </div>
              )}
              <dl className='calendar-day-modal__fields'>
                <div>
                  <dt>Type</dt>
                  <dd>{inspection.inspectionType || '—'}</dd>
                </div>
                <div>
                  <dt>Inspector</dt>
                  <dd>{inspection.inspector}</dd>
                </div>
                <div>
                  <dt>Submitted</dt>
                  <dd>{inspection.inspectionDOI}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{inspection.inspectionStatus || '—'}</dd>
                </div>
              </dl>
              <Link
                className='calendar-day-modal__link'
                to={`/divisions/${divisionId}/projects/${inspection.projectId}/inspections`}
                onClick={onClose}
              >
                View project
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
};

export default CalendarDayModal;
